import type { Task } from '../services/taskService'

/**
 * How the task list is organised (owner 2026-09-05: "optimize /tasks for a
 * good user experience"). A list of things to do answers one question first —
 * what needs me, and when — so the default grouping is by attention, not by
 * the goal a task hangs under:
 *
 *   Doing now     started and not finished
 *   Needs a look  due today or earlier, promised for today, urgent without a
 *                 date, or past a hard complete-by (lapsed: "how did it go?")
 *   This week     due within seven days, or windowed to this week
 *   Later         everything else still open: this month, someday, no time
 *                 set, parked, paused, backlog
 *   Done          finished or dropped — collapsed, newest first
 *
 * "Goal" and "Life area" groupings keep the older answer ("what belongs
 * where") one click away. Pure functions, no React and no runtime imports,
 * so the rules can be probed from node (npm run test:tasks).
 */
export type SectionKey = 'doing' | 'attention' | 'week' | 'later' | 'done'
/** A quick action (action item) rendered as a row carries its own id here. */
export type RowTask = Task & { __actionItemId?: string; __parentTaskId?: string }
export const actionItemIdOf = (t: Task): string | undefined => (t as RowTask).__actionItemId
/** The task an action-item row is a step of, when it has one. */
export const parentTaskIdOf = (t: Task): string | undefined => (t as RowTask).__parentTaskId
export type GroupBy = 'when' | 'goal' | 'area' | 'none'
/** Order inside a group: the section's own attention order, or by a time
 *  the person can see on the row (owner 2026-09-05: created/updated sorting). */
export type SortBy = 'attention' | 'created' | 'updated'
export type SortDir = 'asc' | 'desc'
export interface SortSpec { by: SortBy; dir: SortDir }

export interface Group {
  key: string
  title: string
  /** One quiet line under the heading when the group is empty (only the
   *  attention group renders empty — the good news is worth a line). */
  quiet?: string
  items: Task[]
  collapsedByDefault?: boolean
}

export const SECTION_TITLE: Record<SectionKey, string> = {
  doing: 'Doing now',
  attention: 'Needs a look',
  week: 'This week',
  later: 'Later',
  done: 'Done',
}
const SECTION_ORDER: SectionKey[] = ['doing', 'attention', 'week', 'later', 'done']

const PRIORITY_WEIGHT: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
const WINDOW_WEIGHT: Record<string, number> = { today: 0, this_week: 1, this_month: 2, someday: 3 }

export const isDone = (t: Pick<Task, 'status'>): boolean => t.status === 'completed' || t.status === 'cancelled'
/** Anything not finished, dropped or let go counts as open — including
 *  statuses this page did not know about ("parked" reached prod before it
 *  reached the type). */
export const isOpen = (t: Pick<Task, 'status'>): boolean => !isDone(t) && t.status !== 'lapsed'
/** Start is offered to anything open that has not started — a deny-list, so a
 *  status the page did not know (the engines file tasks as `active`) still gets
 *  the verb. In progress has its Done; done and let go have Reopen. */
export const canStart = (t: Pick<Task, 'status'>): boolean => isOpen(t) && t.status !== 'in_progress'

/** Whole local days from `today` to a YYYY-MM-DD (negative = past), or null. */
export function daysUntil(dateStr: string | null | undefined, today: Date): number | null {
  if (!dateStr) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  if (!m) return null
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target.getTime() - base.getTime()) / 86_400_000)
}

export function sectionOf(t: Task, today: Date): SectionKey {
  if (isDone(t)) return 'done'
  if (t.status === 'lapsed') return 'attention'
  if (t.status === 'in_progress') return 'doing'
  if (t.status === 'on_hold' || (t.status as string) === 'parked' || t.user_tier === 'backlog') return 'later'
  const days = daysUntil(t.due_date, today)
  if (days != null) return days <= 0 ? 'attention' : days <= 7 ? 'week' : 'later'
  if (t.target_window === 'today') return 'attention'
  if (t.priority === 'urgent') return 'attention'
  if (t.target_window === 'this_week') return 'week'
  return 'later'
}

const dueStamp = (t: Task): string => (t.due_date ? `${t.due_date.slice(0, 10)}T${t.due_time || '23:59'}` : '9999-12-31')
const finishedStamp = (t: Task): string => String(t.finished_at || t.completion_date || t.lapsed_at || t.updated_at || '')
const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)
const setAside = (t: Task): boolean => t.status === 'on_hold' || (t.status as string) === 'parked' || t.user_tier === 'backlog'

export function sortSection(items: Task[], key: SectionKey): Task[] {
  const arr = [...items]
  switch (key) {
    case 'doing':
      return arr.sort((a, b) => cmp(String(b.started_at || ''), String(a.started_at || '')))
    case 'attention':
      return arr.sort((a, b) =>
        cmp(a.status === 'lapsed' ? 1 : 0, b.status === 'lapsed' ? 1 : 0)
        || cmp(dueStamp(a), dueStamp(b))
        || cmp(PRIORITY_WEIGHT[a.priority] ?? 2, PRIORITY_WEIGHT[b.priority] ?? 2))
    case 'week':
      return arr.sort((a, b) =>
        cmp(dueStamp(a), dueStamp(b))
        || cmp(WINDOW_WEIGHT[a.target_window || ''] ?? 4, WINDOW_WEIGHT[b.target_window || ''] ?? 4)
        || cmp(PRIORITY_WEIGHT[a.priority] ?? 2, PRIORITY_WEIGHT[b.priority] ?? 2))
    case 'later':
      // Nearest first: a date, then this month, then someday / no time set;
      // what was deliberately set aside (paused, parked, backlog) comes last.
      return arr.sort((a, b) =>
        cmp(setAside(a) ? 1 : 0, setAside(b) ? 1 : 0)
        || cmp(dueStamp(a), dueStamp(b))
        || cmp(WINDOW_WEIGHT[a.target_window || ''] ?? 3, WINDOW_WEIGHT[b.target_window || ''] ?? 3)
        || cmp(PRIORITY_WEIGHT[a.priority] ?? 2, PRIORITY_WEIGHT[b.priority] ?? 2)
        || cmp(String(b.created_at || ''), String(a.created_at || '')))
    case 'done':
      return arr.sort((a, b) => cmp(finishedStamp(b), finishedStamp(a)))
  }
}

/** The row's time for a time sort. Created falls back to updated for rows
 *  that never recorded one; an unknown stamp sorts last either way. */
export function timeKey(t: Task, by: 'created' | 'updated'): string {
  return String((by === 'created' ? (t.created_at || t.updated_at) : (t.updated_at || t.created_at)) || '')
}

export function sortByTime(items: Task[], by: 'created' | 'updated', dir: SortDir): Task[] {
  return [...items].sort((a, b) => {
    const ka = timeKey(a, by), kb = timeKey(b, by)
    if (!ka || !kb) return cmp(ka ? 0 : 1, kb ? 0 : 1)
    return dir === 'desc' ? cmp(kb, ka) : cmp(ka, kb)
  })
}

/** Title / description / parent / life area contain every word of the query. */
export function matchesQuery(t: Task, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [t.title, t.description, t.parent?.title, t.life_area, ...(t.life_areas || []), t.location]
    .filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((w) => hay.includes(w))
}

export interface Summary { open: number; doing: number; attention: number; done: number }

export function summarize(tasks: Task[], today: Date): Summary {
  const s: Summary = { open: 0, doing: 0, attention: 0, done: 0 }
  for (const t of tasks) {
    const k = sectionOf(t, today)
    if (k === 'done') s.done += 1
    else {
      s.open += 1
      if (k === 'doing') s.doing += 1
      if (k === 'attention') s.attention += 1
    }
  }
  return s
}

export function summaryWords(s: Summary): string {
  if (s.open === 0 && s.done === 0) return 'Nothing here yet'
  const parts: string[] = []
  parts.push(s.open === 0 ? 'Nothing open' : `${s.open} open`)
  // The same words as the section heading (North Star rule 2: one fact, one
  // expression) — the line said "in hand" over a section called "Doing now".
  if (s.doing) parts.push(`${s.doing} doing now`)
  if (s.attention) parts.push(`${s.attention} need${s.attention === 1 ? 's' : ''} a look`)
  parts.push(`${s.done} done`)
  return parts.join(' · ')
}

export function groupTasks(tasks: Task[], by: GroupBy, today: Date, labelArea: (value: string) => string = (v) => v, sort: SortSpec = { by: 'attention', dir: 'desc' }): Group[] {
  const groups = buildGroups(tasks, by, today, labelArea)
  if (sort.by === 'attention') return groups
  return groups.map((g) => ({ ...g, items: sortByTime(g.items, sort.by as 'created' | 'updated', sort.dir) }))
}

const attentionOrder = (today: Date) => (a: Task, b: Task) =>
  cmp(SECTION_ORDER.indexOf(sectionOf(a, today)), SECTION_ORDER.indexOf(sectionOf(b, today)))
  || cmp(dueStamp(a), dueStamp(b))
  || cmp(PRIORITY_WEIGHT[a.priority] ?? 2, PRIORITY_WEIGHT[b.priority] ?? 2)

function buildGroups(tasks: Task[], by: GroupBy, today: Date, labelArea: (value: string) => string): Group[] {
  if (by === 'when') {
    const buckets: Record<SectionKey, Task[]> = { doing: [], attention: [], week: [], later: [], done: [] }
    for (const t of tasks) buckets[sectionOf(t, today)].push(t)
    const anyOpen = SECTION_ORDER.some((k) => k !== 'done' && buckets[k].length > 0)
    return SECTION_ORDER
      .map((k): Group => ({
        key: k,
        title: SECTION_TITLE[k],
        items: sortSection(buckets[k], k),
        quiet: k === 'attention' && anyOpen ? 'Nothing is pressing right now.' : undefined,
        collapsedByDefault: k === 'done',
      }))
      .filter((g) => g.items.length > 0 || g.quiet)
  }

  const open = tasks.filter((t) => sectionOf(t, today) !== 'done')
  const done = sortSection(tasks.filter((t) => sectionOf(t, today) === 'done'), 'done')

  // No grouping: one flat list of what is open, attention order unless a time
  // sort says otherwise; done rows still in their own collapsed group.
  if (by === 'none') {
    const groups: Group[] = []
    if (open.length) groups.push({ key: 'open', title: 'Open', items: [...open].sort(attentionOrder(today)) })
    if (done.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: done, collapsedByDefault: true })
    return groups
  }

  // Goal / life area: open tasks under their heading, each heading's rows in
  // attention order; done rows in one trailing collapsed group.
  const map = new Map<string, Group>()
  const orphanKey = by === 'goal' ? 'none' : 'area:none'
  for (const t of open) {
    let key = orphanKey
    let title = by === 'goal' ? 'On their own' : 'No life area'
    if (by === 'goal' && t.parent?.title) { key = `${t.parent.type}:${t.parent.id}`; title = t.parent.title }
    if (by === 'area') {
      const area = t.life_area || t.parent?.life_area || null
      if (area) { key = `area:${area}`; title = labelArea(area) }
    }
    if (!map.has(key)) map.set(key, { key, title, items: [] })
    map.get(key)!.items.push(t)
  }
  const groups = [...map.values()]
  for (const g of groups) g.items.sort(attentionOrder(today))
  groups.sort((a, b) => cmp(a.key === orphanKey ? 1 : 0, b.key === orphanKey ? 1 : 0) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
  if (done.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: done, collapsedByDefault: true })
  return groups
}
